/**
 * NIST SP 800-63B Password Policy Utility
 * Enforces strong password complexity:
 * - Minimum 12 characters
 * - Uppercase letter (A-Z)
 * - Lowercase letter (a-z)
 * - Number (0-9)
 * - Special character (!@#$%^&*()_+-=[]{}|;:,.<>?)
 */

export const PASSWORD_REQUIREMENTS = [
  { id: "length", label: "At least 12 characters", validator: (pwd) => pwd && pwd.length >= 12 },
  { id: "uppercase", label: "At least 1 uppercase letter (A-Z)", validator: (pwd) => /[A-Z]/.test(pwd) },
  { id: "lowercase", label: "At least 1 lowercase letter (a-z)", validator: (pwd) => /[a-z]/.test(pwd) },
  { id: "number", label: "At least 1 number (0-9)", validator: (pwd) => /[0-9]/.test(pwd) },
  { id: "special", label: "At least 1 special character (!@#$%^&*...)", validator: (pwd) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pwd) }
];

/**
 * Validate password against NIST SP 800-63B policy
 * @param {string} password
 * @returns {{ isValid: boolean, passedCount: number, totalCount: number, requirements: Record<string, boolean> }}
 */
export function validatePassword(password = "") {
  const pwd = password || "";
  const requirements = {};
  let passedCount = 0;

  PASSWORD_REQUIREMENTS.forEach((req) => {
    const passed = req.validator(pwd);
    requirements[req.id] = passed;
    if (passed) passedCount++;
  });

  return {
    isValid: passedCount === PASSWORD_REQUIREMENTS.length,
    passedCount,
    totalCount: PASSWORD_REQUIREMENTS.length,
    requirements
  };
}

/**
 * Calculate visual strength score (0 to 4)
 */
export function calculateStrengthScore(password = "") {
  const { passedCount } = validatePassword(password);
  if (!password) return 0;
  if (passedCount <= 2) return 1; // Weak
  if (passedCount <= 3) return 2; // Fair
  if (passedCount === 4) return 3; // Good
  return 4; // Strong (Compliant)
}

/**
 * Generate a cryptographically secure, NIST-compliant 16-character password
 */
export function generateCompliantPassword(length = 16) {
  const uppercase = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // omitted I, O
  const lowercase = "abcdefghijkmnpqrstuvwxyz"; // omitted l, o
  const numbers = "23456789"; // omitted 0, 1
  const specials = "!@#$%^&*()_+-=";

  const allChars = uppercase + lowercase + numbers + specials;
  const getCrypto = () => {
    if (typeof globalThis !== "undefined" && globalThis.crypto) return globalThis.crypto;
    if (typeof window !== "undefined" && window.crypto) return window.crypto;
    throw new Error("Crypto API unavailable");
  };

  const crypto = getCrypto();
  const randomBytes = new Uint8Array(length);
  crypto.getRandomValues(randomBytes);

  // Ensure at least one from each category
  const guaranteed = [
    uppercase[randomBytes[0] % uppercase.length],
    lowercase[randomBytes[1] % lowercase.length],
    numbers[randomBytes[2] % numbers.length],
    specials[randomBytes[3] % specials.length]
  ];

  const remainingLength = length - guaranteed.length;
  const remaining = [];
  for (let i = 0; i < remainingLength; i++) {
    remaining.push(allChars[randomBytes[4 + i] % allChars.length]);
  }

  // Shuffle array
  const full = [...guaranteed, ...remaining];
  for (let i = full.length - 1; i > 0; i--) {
    const j = randomBytes[i] % (i + 1);
    [full[i], full[j]] = [full[j], full[i]];
  }

  return full.join("");
}
